import React, { useEffect } from 'react';
import '../styles/pages/Terms.scss';
import { Helmet } from 'react-helmet';

const Terms = () => {
  // Set page title
  useEffect(() => {
    document.title = "Terms and Conditions - FirstWhistle";
  }, []);

  return (
    <div className="terms-page">
      <Helmet>
        <title>Terms and Conditions | FirstWhistle</title>
        <meta name="description" content="FirstWhistle's Terms and Conditions of Use govern your use of our football team management application and services." />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://firstwhistle.app/terms" />
        <meta property="og:title" content="Terms and Conditions | FirstWhistle" />
        <meta property="og:description" content="FirstWhistle's Terms and Conditions of Use govern your use of our football team management application and services." />
        <meta property="og:site_name" content="FirstWhistle" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:url" content="https://firstwhistle.app/terms" />
        <meta name="twitter:title" content="Terms and Conditions | FirstWhistle" />
        <meta name="twitter:description" content="FirstWhistle's Terms and Conditions of Use govern your use of our football team management application and services." />
        
        <link rel="canonical" href="https://firstwhistle.app/terms" />
      </Helmet>

      {/* Main Content Section */}
      <section className="terms-content-section">
        <div className="terms-container">
          <div className="terms-header">
            <h1>Terms and Conditions of Use</h1>
            <div className="terms-meta">
              <p><strong>Last Updated:</strong> 27 July 2025</p>
              <p><strong>Version:</strong> 1.0</p>
            </div>
          </div>

          <div className="terms-content">
            <section className="policy-section">
              <h2>1. Introduction</h2>
              <p>FirstWhistle is a football team management application developed and operated by Media Meadow Ltd t/a Pebble Designs</p>
              <p>These terms constitute a legally binding agreement between you (the user) and Media Meadow Ltd regarding your use of the FirstWhistle mobile application and web platform</p>
              <p>Users must be at least 13 years old to create an account independently; users under 13 may only access the app through a parent or guardian account.</p>
              <p>The app is designed for football teams, coaches, players, and parents to manage match scheduling, player attendance, team communications, and performance tracking.</p>
            </section>

            <section className="policy-section">
              <h2>2. Acceptance of Terms</h2>
              <p>By downloading, installing, or using the FirstWhistle app, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions</p>
              <p>If you do not agree to these terms, you must not use the application.</p>
              <p>These terms should be read in conjunction with our Privacy Policy and Safeguarding Policy, which are incorporated by reference</p>
              <p>Links to these policies are available within the app and on our website at <a href="https://firstwhistle.app" target="_blank" rel="noopener noreferrer">firstwhistle.app</a></p>
            </section>

            <section className="policy-section">
              <h2>3. User Responsibilities</h2>
              <ul>
                <li>You must provide accurate, current, and complete information during registration and keep your account details updated</li>
                <li>You are responsible for maintaining the confidentiality of your login credentials and for all activities that occur under your account.</li>
                <li>You must use the app only for its intended purpose of football team management and related activities.</li>
              </ul>
              <p>You agree not to:</p>
              <ul>
                <li>Harass, abuse, or intimidate other users</li>
                <li>Impersonate any person or entity or misrepresent your affiliation</li>
                <li>Use the app for any illegal or unauthorised purpose</li>
                <li>Attempt to gain unauthorised access to other users' accounts or the app's systems</li>
                <li>Share inappropriate content, including but not limited to offensive language or images</li>
                <li>Use the app to send spam, unsolicited messages, or commercial communications</li>
              </ul>
            </section>

            <section className="policy-section">
              <h2>4. Account and Access</h2>
              <ul>
                <li><strong>Player Accounts:</strong> Available to individuals aged 13 and above who are members of football teams</li>
                <li><strong>Coach/Manager Accounts:</strong> Available to team coaches, managers, and administrators who are responsible for team management</li>
                <li><strong>Parent Accounts:</strong> Available to parents and guardians of players under 18 years old for managing their children's football activities</li>
              </ul>
              <ul>
                <li>Each user may only maintain one account per user type; multiple accounts for the same person are not permitted</li>
                <li>We reserve the right to terminate or suspend accounts that violate these terms, engage in fraudulent activity, or pose a risk to other users</li>
                <li>Account termination may result in the loss of access to your data and team information.</li>
              </ul>
            </section>

            <section className="policy-section">
              <h2>5. Data Privacy</h2>
              <p>We collect and process the following types of data:</p>
              <ul>
                <li>Personal information (name, email, phone number, date of birth)</li>
                <li>Team and match information (attendance, performance statistics, match results)</li>
                <li>Location data for match venues and training sessions</li>
                <li>Device information for push notifications and app functionality</li>
                <li>Payment information for subscription services (processed securely through Stripe)</li>
              </ul>

              <p>Your data is used to:</p>
              <ul>
                <li>Provide match scheduling and team management services</li>
                <li>Track player performance and team statistics</li>
                <li>Send notifications about matches, training sessions, and team updates</li>
                <li>Process payments for Pro subscriptions and match fees</li>
                <li>Improve app functionality and user experience</li>
              </ul>
              <p>We do not sell your personal data to third parties</p>

              <p>Data may be shared with:</p>
              <ul>
                <li>Other team members (as necessary for team management)</li>
                <li>Payment processors (Stripe) for subscription and payment processing</li>
                <li>Service providers who assist in app operation (subject to confidentiality agreements)</li>
                <li>Law enforcement when required by law or to protect user safety</li>
              </ul>
              <p>For detailed information about data handling, please refer to our <a href="/privacy">Privacy Policy</a></p>
            </section>

            <section className="policy-section">
              <h2>6. Safeguarding and Safety</h2>
              <ul>
                <li>FirstWhistle is committed to safeguarding children and vulnerable adults in accordance with UK regulations</li>
                <li>We have implemented appropriate technical and organisational measures to protect user data and ensure safe use of the platform</li>
                <li>Users must report any inappropriate behaviour, content, or safeguarding concerns through the app's reporting features or by contacting our safeguarding officer</li>
                <li>Parents and guardians are responsible for monitoring their children's use of the app and ensuring appropriate online behaviour</li>
                <li>Coaches and team managers have a duty to maintain professional boundaries and report any safeguarding concerns</li>
                <li>We reserve the right to investigate and take appropriate action regarding any reported safeguarding issues</li>
                <li>Users found to be engaging in inappropriate behaviour may have their accounts suspended or terminated</li>
              </ul>
            </section>

            <section className="policy-section">
              <h2>7. Content and Intellectual Property</h2>
              <p>FirstWhistle and all associated content, including but not limited to the app design, code, logos, and branding, are owned by Pebble Designs Ltd</p>
              <p>Users retain ownership of content they create within the app (such as match reports, team communications, and player statistics)</p>
              <p>By using the app, you grant FirstWhistle a non-exclusive, royalty-free licence to use, store, and display your content for the purpose of providing the service</p>
              <p>You may not:</p>
              <ul>
                <li>Copy, reproduce, or distribute the app or its content without permission</li>
                <li>Reverse engineer, decompile, or attempt to extract the app's source code</li>
                <li>Use the app's content for commercial purposes without written consent</li>
                <li>Remove or alter any copyright, trademark, or proprietary notices</li>
              </ul>
            </section>

            <section className="policy-section">
              <h2>8. Payment and Subscriptions</h2>
              <p>FirstWhistle offers both free and premium (Pro) subscription tiers</p>
              <ul>
                <li><strong>Pro Subscription Features:</strong> Enhanced statistics, advanced team management tools, priority support, and additional features</li>
                <li><strong>Pricing:</strong> Monthly subscription (£2.50/month) or annual subscription (£25.00/year)</li>
                <li><strong>Payment Processing:</strong> All payments are processed securely through Stripe; we do not store your payment card details</li>
                <li><strong>Billing:</strong> Subscriptions are billed in advance on a recurring basis</li>
                <li><strong>Cancellation:</strong> You may cancel your subscription at any time through your account settings or Stripe Customer Portal; cancellation will take effect at the end of the current billing period</li>
                <li><strong>Refunds:</strong> Refunds are provided at our discretion and in accordance with applicable consumer protection laws</li>
                <li><strong>Failed Payments:</strong> If a payment fails, we will attempt to retry the payment; continued failure may result in subscription suspension</li>
                <li><strong>Price Changes:</strong> We may change subscription prices with 30 days' notice; continued use after price changes constitutes acceptance of new pricing</li>
              </ul>
            </section>

            <section className="policy-section">
              <h2>9. Limitation of Liability</h2>
              <p>FirstWhistle is provided "as is" without warranties of any kind, either express or implied</p>
              <p>We do not guarantee that the app will be uninterrupted, error-free, or secure</p>
              <p>We are not responsible for:</p>
              <ul>
                <li>Match cancellations, postponements, or venue changes</li>
                <li>Missed notifications or communication delays</li>
                <li>Data loss due to technical issues or user error</li>
                <li>Actions or conduct of other users</li>
                <li>External factors affecting match scheduling or team activities</li>
              </ul>
              <p>Our liability is limited to the amount you have paid for the service in the 12 months preceding the claim</p>
              <p>We are not liable for any indirect, incidental, special, or consequential damages</p>
              <p>These limitations do not apply to liability for death or personal injury caused by our negligence</p>
            </section>

            <section className="policy-section">
              <h2>10. Updates to the Terms</h2>
              <p>We reserve the right to modify these terms at any time</p>
              <p>Users will be notified of significant changes through:</p>
              <ul>
                <li>In-app notifications</li>
                <li>Email communications to registered users</li>
                <li>Updates posted on our website</li>
              </ul>
              <p>Continued use of the app after changes are published constitutes acceptance of the updated terms</p>
              <p>If you do not agree to updated terms, you must stop using the app and delete your account</p>
              <p>The date of the most recent update will be displayed at the top of these terms</p>
            </section>

            <section className="policy-section">
              <h2>11. Contact Information</h2>
              <p>For general inquiries, technical support, or account issues: <a href="mailto:support@firstwhistle.app">support@firstwhistle.app</a></p>
              <p>For safeguarding concerns or reports: <a href="mailto:support@firstwhistle.app">support@firstwhistle.app</a></p>
              <p>For privacy and data protection questions: <a href="mailto:support@firstwhistle.app">support@firstwhistle.app</a></p>
              <p>For payment and subscription issues: <a href="mailto:support@firstwhistle.app">support@firstwhistle.app</a></p>
              <p>For legal matters or disputes: <a href="mailto:support@firstwhistle.app">support@firstwhistle.app</a></p>
              <p><strong>Our registered address:</strong> FYI Media Meadow, Freedom Works, Hastings, East Sussex, TN38 0JG, UK</p>
              <p>We aim to respond to all inquiries within 48 hours during business days</p>
            </section>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Terms; 